import { Client } from 'openapi-fetch';

import {
  SquirrelMoneyCoreContractsCreateWithdrawalForm as CreateWithdrawalForm,
  SquirrelMoneyCoreContractsInvestorEntityDto as InvestorEntityDto,
  SquirrelMoneyCoreContractsUpdateInvestorEntityForm as UpdateInvestorEntityForm,
  paths as accountOverhaulPaths,
} from '@core/schemas/account-overhaul-schema';
import { paths as api1Paths } from '@core/schemas/api1-schema';
import { paths as api2Paths } from '@core/schemas/api2-schema';
import { isFeatureEnabled } from '@core/utilities/feature-flags';

/**
 * TODO: Overhaul — delete this file at cut-over. It is a mock `ACCOUNT_OVERHAUL_CLIENT` for the investor
 * entity / withdrawal routes that are not served by the overhaul backend yet. Rather than fixture data, it
 * calls the REAL legacy endpoints (on-call account + withdrawal) and maps them into the overhaul shape, so the
 * overhaul-shaped `EntityQueriesService` gets proper live data now. At cut-over, remove this file and each
 * consuming screen/dialog's `ACCOUNT_OVERHAUL_CLIENT` provider override — the same queries then hit the real
 * overhaul endpoints unchanged.
 *
 * Because it *overrides* `ACCOUNT_OVERHAUL_CLIENT`, it bypasses that client's built-in "flag off → refuse"
 * backstop, so it carries its own: gated on `accountOverhaul` (off in prod). The owning routes are already
 * flag-guarded, so today the mock is unreachable in prod — this guarantees a redirect to legacy endpoints
 * can NEVER run off-flag even if a consumer is later imported by an un-gated prod component.
 */

interface GetInit {
  params: { path: { investorEntityId: number } };
}
interface PostInit {
  params: { path: { investorEntityId: number } };
  body: CreateWithdrawalForm;
}
interface PutInit {
  params: { path: { investorEntityId: number } };
  body: UpdateInvestorEntityForm;
}
interface DeleteInit {
  params: { path: { investorEntityId: number; withdrawalId: number } };
}

/**
 * A mock `ACCOUNT_OVERHAUL_CLIENT` backed by the live legacy API1 / API2 clients (see file header). Unlike the
 * fixture-based overhaul mocks, this one has no in-memory store — it maps live legacy responses — so it needs
 * the real clients injected. Provide it on the **narrow, lazy, flag-gated** screen/dialog that owns
 * `EntityQueriesService` (the withdraw-to-bank dialog and the Your Wallet screen), via a factory so DI
 * supplies those clients:
 *
 *   providers: [
 *     EntityQueriesService,
 *     {
 *       provide: ACCOUNT_OVERHAUL_CLIENT,
 *       useFactory: (api1, api2) => createEntityMockClient({ api1, api2 }),
 *       deps: [API1_CLIENT, API2_CLIENT],
 *     },
 *   ]
 *
 * Only handles the entity read + withdrawal routes — never provide it at route/root level, or it will
 * shadow real overhaul calls in the subtree.
 */
export function createEntityMockClient(deps: {
  api1: Client<api1Paths>;
  api2: Client<api2Paths>;
}): Client<accountOverhaulPaths> {
  // Backstop mirroring the real ACCOUNT_OVERHAUL_CLIENT: never redirect overhaul calls to legacy endpoints
  // unless the overhaul flag is on (it's off in prod). Off → refuse loudly instead.
  if (!isFeatureEnabled('accountOverhaul')) return createUnavailableClient();

  // The legacy on-call read carries the wallet balance, the linked bank-account id AND the withdrawals list.
  // `pageIndex` is 1-based (the On-call screen fetches with `pageIndex: 1`); page 0 returns valid stats but an
  // empty `withdrawals` page, so pending withdrawals never surface. Fetch a generous page to include them all.
  const fetchOnCall = (entityId: number) =>
    deps.api2.GET('/v2/on-call-accounts/{entityId}', {
      params: { path: { entityId }, query: { pageIndex: 1, pageSize: 50 } },
    });

  // The legacy entity read carries the entity type, PIR and tax-residency the create-managed-fund flow needs
  // (the on-call read carries none of them). Mapped into the overhaul entity shape alongside the wallet fields.
  const fetchEntity = (entityId: number) =>
    deps.api1.GET('/v2/investor-entities/{investorEntityId}', {
      params: { path: { investorEntityId: entityId } },
    });

  return {
    async GET(path: string, init: GetInit) {
      if (path === '/v1/investor-entities/{investorEntityId}') {
        const entityId = init.params.path.investorEntityId;
        const [onCallResult, entityResult] = await Promise.all([fetchOnCall(entityId), fetchEntity(entityId)]);
        const { data, error, response } = onCallResult;
        if (error) return { data: undefined, error, response };
        // A failed legacy entity read degrades gracefully: the wallet fields (from the on-call read) still
        // return, and the managed-fund fields fall back to their defaults.
        const legacy = entityResult.error ? undefined : entityResult.data;
        const entity: InvestorEntityDto = {
          investorEntityId: entityId,
          displayName: legacy?.displayName ?? undefined,
          walletBalance: data?.entityStatistics?.fundsAvailableForInvesting ?? null,
          bankAccountId: data?.bankAccount?.bankAccountId ?? undefined,
          // The on-call read nests the investor entity, which carries the wholesale flag — pass it through so
          // overhaul screens can tell wholesale from retail investors (the overhaul entity endpoint sets it).
          isWholesale: data?.entity?.isWholesale ?? false,
          // …and the managed-fund distribution instruction — the account-detail Managed Fund distribution row reads
          // it. The overhaul entity endpoint carries `distribution` too (verified in the schema), so this mapping
          // vanishes at cut-over with the consumer reading it unchanged.
          distribution: data?.entity?.distribution,
          // Mock heuristic for the real `depositReceived` flag (the overhaul entity endpoint sets it): the
          // investor has deposited if they have any financial footprint — Wallet funds now, funds already
          // invested (deposited then invested it all), or any withdrawal (you can only withdraw what you
          // first deposited). Replaced by the real field at cut-over.
          depositReceived:
            (data?.entityStatistics?.fundsAvailableForInvesting ?? 0) > 0 ||
            (data?.entityStatistics?.fundsInvested ?? 0) > 0 ||
            (data?.withdrawals?.items?.length ?? 0) > 0,
          // Managed-fund flow fields, mapped from the legacy entity. These gate the create-managed-fund flow's
          // Monthly Income Fund option — its registration prerequisites: IRD number, RWT rate (`withholdingTaxId`)
          // and completed Nature & Purpose (see the Managed Funds KB). The foreign-tax (FATCA/CRS) prerequisite is
          // NOT read from the entity: a Person entity's `entityTaxResidency` is null, so that gate reads the USER's
          // tax residency (`UserQueriesService.foreignTaxResidencyComplete`) instead.
          entityTypeId: legacy?.entityTypeId,
          prescribedInvestorRate: legacy?.prescribedInvestorRate ?? null,
          isNotifiedForeignInvestor: legacy?.isNotifiedForeignInvestor ?? false,
          irdNumber: legacy?.irdNumber ?? null,
          withholdingTaxId: legacy?.withholdingTaxId ?? null,
          natureAndPurposeCompleted: legacy?.natureAndPurposeCompleted ?? false,
          // A trust's deed date — a managed-fund registration prerequisite for trusts (see `trustDeedDateRequired`).
          trustDeedDate: legacy?.trustDeedDate ?? null,
          // Whether the entity has passed system verification — a managed-fund registration prerequisite (see
          // `isVerified`). From the entity read (the same source as the legacy `selectInvestorIsVerified`).
          isVerified: legacy?.isVerified ?? false,
          // Whether the entity is enrolled in the managed fund — read from the on-call read's nested entity, the
          // SAME source the account list derives the managed-fund account from (so the two never disagree). The
          // account-detail screen pairs it with the valuations read to show a "being set up" state; the Home card
          // then polls valuations — not this flag — to know when the provider has finished setting the fund up.
          inManagedFund: data?.entity?.inManagedFund ?? false,
          // A rejected provider registration — the Home managed-fund card surfaces it as an error.
          lastAdminisRegistrationRejected: legacy?.lastAdminisRegistrationRejected ?? false,
        };
        return { data: entity, error: undefined, response };
      }
      if (path === '/v1/investor-entities/{investorEntityId}/withdrawals') {
        // Map the legacy on-call withdrawals to the overhaul WithdrawalDto shape — its id is `withdrawalId`
        // (the legacy `withdrawId`); the rest of the fields line up.
        const { data, error, response } = await fetchOnCall(init.params.path.investorEntityId);
        if (error) return { data: undefined, error, response };
        const items = (data?.withdrawals?.items ?? []).map(w => ({
          withdrawalId: w.withdrawId,
          investmentAccountId: w.investmentAccountId,
          amount: w.amount,
          createDateNzt: w.createDateNzt,
          cancelled: w.cancelled,
          processedDateNzt: w.processedDateNzt,
          completedDateNzt: w.completedDateNzt,
          reference: w.reference,
          effectiveReference: w.effectiveReference,
        }));
        return { data: { items, metadata: data?.withdrawals?.metadata }, error: undefined, response };
      }
      throw new Error(`entity mock: unhandled GET ${path}`);
    },

    async POST(path: string, init: PostInit) {
      if (path === '/v1/investor-entities/{investorEntityId}/withdrawals') {
        // The overhaul withdrawal is entity-scoped; the legacy endpoint is bank-account-scoped, so resolve
        // the entity's linked account first, then submit through it.
        const { data: onCall, error, response } = await fetchOnCall(init.params.path.investorEntityId);
        if (error) return { data: undefined, error, response };
        const bankAccountId = onCall?.bankAccount?.bankAccountId;
        if (bankAccountId == null) {
          return {
            data: undefined,
            error: { message: 'No linked bank account' },
            response: new Response(null, { status: 400 }),
          };
        }
        return deps.api1.POST('/v1/withdrawals/bank-accounts/{bankAccountId}', {
          params: { path: { bankAccountId } },
          body: { amount: init.body.amount, reference: init.body.reference },
        });
      }
      throw new Error(`entity mock: unhandled POST ${path}`);
    },

    async PUT(path: string, init: PutInit) {
      if (path === '/v1/investor-entities/{investorEntityId}') {
        // Managed-fund registration, mirroring the "Register for Managed Funds" form: fetch the current entity,
        // merge the flow's fields + PDS acceptance (the PUT replaces the WHOLE entity, so its existing values must
        // be sent or they'd be cleared), then PUT. Required fields the flow doesn't collect (IRD, RWT, and a
        // trust's deed date) ride through from the fetched entity; the BE rejects if they're genuinely absent.
        //
        // TODO: Overhaul — PDS acceptance. The overhaul `UpdateInvestorEntityForm` carries no
        // `managedFundDisclosureStatementAccepted` and the only PDS-accept endpoint is admin-only, so acceptance is
        // injected here into the legacy payload as an interim no-op/mock. A separate ticket owns updating PDS
        // acceptance (its design is still in the air and will likely introduce its own overhaul endpoints); wire
        // this to that once it lands.
        const entityId = init.params.path.investorEntityId;
        const current = await deps.api1.GET('/v2/investor-entities/{investorEntityId}', {
          params: { path: { investorEntityId: entityId } },
        });
        if (current.error) return { data: undefined, error: current.error, response: current.response };
        return deps.api1.PUT('/v2/investor-entities/{investorEntityId}', {
          params: { path: { investorEntityId: entityId } },
          body: {
            ...current.data,
            entityId,
            prescribedInvestorRate: init.body?.prescribedInvestorRate,
            distribution: init.body?.distribution,
            managedFundDisclosureStatementAccepted: true,
          } as never,
        });
      }
      throw new Error(`entity mock: unhandled PUT ${path}`);
    },

    async DELETE(path: string, init: DeleteInit) {
      if (path === '/v1/investor-entities/{investorEntityId}/withdrawals/{withdrawalId}') {
        // The overhaul cancel is entity-scoped; the legacy cancel endpoint is withdrawal-id-scoped.
        return deps.api1.DELETE('/v1/withdrawals/cancel/{withdrawId}', {
          params: { path: { withdrawId: init.params.path.withdrawalId } },
        });
      }
      throw new Error(`entity mock: unhandled DELETE ${path}`);
    },
  } as unknown as Client<accountOverhaulPaths>;
}

/**
 * A client that rejects every call — the off-flag backstop (see `createEntityMockClient`). Matches the real
 * `ACCOUNT_OVERHAUL_CLIENT`'s fail-loud behaviour so an accidental off-flag consumer errors rather than
 * silently reaching legacy endpoints.
 */
function createUnavailableClient(): Client<accountOverhaulPaths> {
  const refuse = () =>
    Promise.reject(
      new Error('Entity mock ACCOUNT_OVERHAUL_CLIENT is unavailable: the accountOverhaul feature flag is off.'),
    );
  return { GET: refuse, POST: refuse, PUT: refuse, DELETE: refuse } as unknown as Client<accountOverhaulPaths>;
}
